import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { CodeBlock } from '../../components/CodeBlock';
import { CodeTabs } from '../../components/CodeTabs';
import { Seo } from '../../components/Seo';
import {
  ANTHROPIC_REQUEST,
  AUTH_HEADERS,
  ENV_VARS,
  ERROR_EXAMPLE,
  GEMINI_STREAM,
  OPENAI_STREAM,
} from '../../data/docsSnippets';
import { BASE_URLS } from '../../data/models';
import { localizedPath } from '../../i18n/config';
import { useLocale } from '../../i18n/useLocale';

const SECTIONS = [
  'start',
  'auth',
  'openai',
  'anthropic',
  'gemini',
  'tools',
  'streaming',
  'models',
  'billing',
  'errors',
  'limits',
] as const;
const FORMATS = ['openai', 'anthropic', 'gemini'] as const;
const ERRORS = ['400', '401', '402', '429', '502', '503'] as const;

export function DocsPage() {
  const { t } = useTranslation();
  const locale = useLocale();
  const title = (id: (typeof SECTIONS)[number]) => t(`docs.${id}.title`);

  return (
    <>
      <Seo title={t('docs.metaTitle')} description={t('docs.metaDescription')} />
      <div className="container page-head">
        <h1>{t('docs.title')}</h1>
        <p className="lead">{t('docs.lead')}</p>
      </div>
      <div className="container docs">
        <nav className="docs-toc" aria-label={t('docs.toc')}>
          <p className="docs-toc-title">{t('docs.toc')}</p>
          <ol>
            {SECTIONS.map((id) => (
              <li key={id}>
                <a href={`#${id}`}>{title(id)}</a>
              </li>
            ))}
          </ol>
        </nav>

        <div className="docs-body">
          <section id="start" aria-labelledby="start-title">
            <h2 id="start-title">{title('start')}</h2>
            <ol className="docs-steps">
              <li>{t('docs.start.s1')}</li>
              <li>{t('docs.start.s2')}</li>
              <li>{t('docs.start.s3')}</li>
              <li>{t('docs.start.s4')}</li>
            </ol>
            <dl className="endpoints">
              {FORMATS.map((format) => (
                <div key={format}>
                  <dt>{t(`landing.code.${format}`)}</dt>
                  <dd>
                    <code>{BASE_URLS[format]}</code>
                  </dd>
                </div>
              ))}
            </dl>
            <CodeTabs />
            <Link className="button button-primary" to={localizedPath(locale, '/app')}>
              {t('docs.start.dashboard')}
            </Link>
          </section>

          <section id="auth" aria-labelledby="auth-title">
            <h2 id="auth-title">{title('auth')}</h2>
            <p>{t('docs.auth.text')}</p>
            <CodeBlock label="headers" code={AUTH_HEADERS} />
          </section>

          <section id="openai" aria-labelledby="openai-title">
            <h2 id="openai-title">{title('openai')}</h2>
            <p>
              <code>POST /v1/chat/completions</code> {t('docs.openai.text')}
            </p>
            <p>{t('docs.openai.stream')}</p>
            <CodeBlock label="request.sh" code={OPENAI_STREAM} />
          </section>

          <section id="anthropic" aria-labelledby="anthropic-title">
            <h2 id="anthropic-title">{title('anthropic')}</h2>
            <p>
              <code>POST /v1/messages</code> {t('docs.anthropic.text')}
            </p>
            <CodeBlock label="request.sh" code={ANTHROPIC_REQUEST} />
          </section>

          <section id="gemini" aria-labelledby="gemini-title">
            <h2 id="gemini-title">{title('gemini')}</h2>
            <p>
              <code>POST /v1beta/models/{'{model}'}:generateContent</code> {t('docs.gemini.text')}
            </p>
            <CodeBlock label="request.sh" code={GEMINI_STREAM} />
          </section>

          <section id="tools" aria-labelledby="tools-title">
            <h2 id="tools-title">{title('tools')}</h2>
            <p>{t('docs.tools.text')}</p>
            <CodeBlock label=".env" code={ENV_VARS} />
          </section>

          <section id="streaming" aria-labelledby="streaming-title">
            <h2 id="streaming-title">{title('streaming')}</h2>
            <p>{t('docs.streaming.text1')}</p>
            <p>{t('docs.streaming.text2')}</p>
          </section>

          <section id="models" aria-labelledby="models-title">
            <h2 id="models-title">{title('models')}</h2>
            <p>{t('docs.models.text')}</p>
            <Link className="text-link" to={localizedPath(locale, '/models')}>
              {t('docs.models.link')}
            </Link>
          </section>

          <section id="billing" aria-labelledby="billing-title">
            <h2 id="billing-title">{title('billing')}</h2>
            <p>{t('docs.billing.text1')}</p>
            <p>{t('docs.billing.text2')}</p>
            <p>{t('docs.billing.text3')}</p>
          </section>

          <section id="errors" aria-labelledby="errors-title">
            <h2 id="errors-title">{title('errors')}</h2>
            <p>{t('docs.errors.text')}</p>
            <table className="docs-table">
              <thead>
                <tr>
                  <th scope="col">{t('docs.errors.status')}</th>
                  <th scope="col">{t('docs.errors.meaning')}</th>
                </tr>
              </thead>
              <tbody>
                {ERRORS.map((code) => (
                  <tr key={code}>
                    <th scope="row">
                      <code>{code}</code>
                    </th>
                    <td>{t(`docs.errors.e${code}`)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <CodeBlock label="response" code={ERROR_EXAMPLE} />
          </section>

          <section id="limits" aria-labelledby="limits-title">
            <h2 id="limits-title">{title('limits')}</h2>
            <p>{t('docs.limits.text')}</p>
          </section>
        </div>
      </div>
    </>
  );
}
